import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import LocalNewsGrid from "./components/LocalNewsGrid";
import NewsletterSection from "../home/components/NewsletterSection";
import SEO from "@/components/SEO";

export default function AviacionRD() {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/actualidad?search=${encodeURIComponent(search.trim())}`);
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F7FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "WebPage",
            name: "Aviación en República Dominicana",
            description: "Cobertura exclusiva de la aviación dominicana: aerolíneas, aeropuertos, estadísticas y operaciones locales.",
            url: 'https://aviacioninsightrd.com/aviacion-rd',
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0B1F3A] pt-32 pb-0 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20panoramic%20view%20Dominican%20Republic%20Caribbean%20coast%20from%20aircraft%20window%2C%20blue%20turquoise%20ocean%2C%20tropical%20island%2C%20lush%20green%20landscape%2C%20beautiful%20Caribbean%20scenery%20from%20flight&width=1920&height=600&seq=rd-hero-1&orientation=landscape"
            alt="Aviación República Dominicana"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/80 to-[#0B1F3A]" />
        </div>
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10 pb-16">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-blue-400 text-sm hover:text-blue-300 cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line text-blue-600" />
            <span className="text-slate-400 text-sm">Aviación en RD</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-3xl">🇩🇴</span>
            <div>
              <h1 className="text-white font-bold leading-none" style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3.2rem)" }}>
                AVIACIÓN EN REPÚBLICA DOMINICANA
              </h1>
              <p className="text-blue-200/70 text-sm mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                El hub aéreo más dinámico del Caribe · Cobertura local exclusiva
              </p>
            </div>
          </div>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="relative max-w-md mt-2">
            <div className="relative flex items-center bg-white/10 border border-white/20 rounded-xl backdrop-blur-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-blue-200/60 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar noticias de aviación en RD..."
                className="w-full pl-9 pr-24 py-3 bg-transparent text-sm text-white placeholder-blue-200/40 focus:outline-none"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              />
              <button
                type="submit"
                className="absolute right-2 px-4 py-1.5 bg-white hover:bg-blue-50 text-[#0B1F3A] text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Buscar
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <LocalNewsGrid />
      </div>

      {/* Stats section — real data from JAC / admin only */}
      <section className="bg-white py-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-7 bg-red-600 rounded-full" />
            <h2 className="text-[#0B1F3A] font-bold" style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.6rem" }}>
              CIFRAS DEL SECTOR
            </h2>
          </div>
          <p className="text-slate-400 text-sm mb-8 ml-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Datos oficiales de la Junta de Aviación Civil (JAC) · República Dominicana
          </p>
          <div className="bg-[#F4F7FB] rounded-2xl border border-slate-100 p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
              <i className="ri-bar-chart-2-line text-slate-400 text-2xl" />
            </div>
            <p className="text-slate-400 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Próximamente estadísticas oficiales del sector
            </p>
            <p className="text-slate-300 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Los datos oficiales de la JAC se publicarán automáticamente al estar disponibles
            </p>
            <Link
              to="/estadisticas"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-semibold rounded-xl hover:bg-[#0d2547] transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <i className="ri-bar-chart-box-line" />
              Ver estadísticas oficiales
            </Link>
          </div>
        </div>
      </section>

      {/* Airlines section */}
      <section className="bg-white py-16 border-t border-slate-100">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          {/* Dominican airlines */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-7 bg-red-600 rounded-full" />
              <h2 className="text-[#0B1F3A] font-bold" style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.6rem" }}>
                AEROLÍNEAS DOMINICANAS
              </h2>
            </div>
            <p className="text-slate-400 text-sm mb-8 ml-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Las aerolíneas nacionales que conectan República Dominicana con el mundo
            </p>
            <div className="bg-[#F4F7FB] rounded-2xl border border-slate-100 p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-flight-takeoff-line text-slate-400 text-2xl" />
              </div>
              <p className="text-slate-400 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Próximamente información de aerolíneas dominicanas
              </p>
              <p className="text-slate-300 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Se mostrará automáticamente al publicarse desde el admin
              </p>
            </div>
          </div>

          {/* Foreign airlines */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-7 bg-blue-600 rounded-full" />
              <h2 className="text-[#0B1F3A] font-bold" style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.6rem" }}>
                AEROLÍNEAS EXTRANJERAS QUE VUELAN A RD
              </h2>
            </div>
            <p className="text-slate-400 text-sm mb-8 ml-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Las principales aerolíneas internacionales con operaciones regulares en República Dominicana
            </p>
            <div className="bg-[#F4F7FB] rounded-2xl border border-slate-100 p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-earth-line text-slate-400 text-2xl" />
              </div>
              <p className="text-slate-400 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Próximamente información de aerolíneas internacionales
              </p>
              <p className="text-slate-300 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Se mostrará automáticamente al publicarse desde el admin
              </p>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSection />
      <Footer />
    </main>
  );
}